'use client';

import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db } from '../lib/firebase';
import { useLocale } from '../context/DynamicLocaleProvider';
import { useToast } from '../context/ToastProvider';
import { GlobalInput } from './GlobalInput';
import { GlobalTextarea } from './GlobalTextarea';
import { GlobalButton } from './GlobalButton';
import GlobalCollapsibleButton from './GlobalCollapsibleButton';
import styles from './CompanySettings.module.css';

interface SponsorshipBadgeSettings {
  enabled: boolean;
  text: string;
  logoUrl: string;
}

interface CompanySettingsProps {
  companyId: string;
  /** Called after a successful write so the parent can refresh displayed company data */
  onCompanyUpdated?: () => void;
}

export const CompanySettings: React.FC<CompanySettingsProps> = ({ companyId, onCompanyUpdated }) => {
  const { t } = useLocale();
  const showToast = useToast();

  const [sponsorshipBadge, setSponsorshipBadge] = useState<SponsorshipBadgeSettings>({
    enabled: false,
    text: '',
    logoUrl: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Microsoft SSO settings
  const [allowedEmailDomains, setAllowedEmailDomains] = useState<string[]>([]);
  const [emailDomainsInput, setEmailDomainsInput] = useState<string>('');
  const [savingEmailDomains, setSavingEmailDomains] = useState(false);

  // Base company data state
  const [baseData, setBaseData] = useState({
    name: '',
    website: '',
    address_street: '',
    address_house_number: '',
    address_zip_code: '',
    address_city: '',
    address_country: '',
  });
  const [savingBaseData, setSavingBaseData] = useState(false);

  // Load company settings from Firebase
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const companyRef = doc(db, 'companies', companyId);
        const companyDoc = await getDoc(companyRef);

        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          const sponsorshipSettings = companyData.settings?.sponsorshipBadge || {};

          setSponsorshipBadge({
            enabled: sponsorshipSettings.enabled ?? false,
            text: sponsorshipSettings.text ?? '',
            logoUrl: sponsorshipSettings.logoUrl ?? '',
          });


          // Load allowed email domains for Microsoft SSO
          const domains = companyData.allowedEmailDomains || [];
          setAllowedEmailDomains(domains);
          setEmailDomainsInput(domains.join(', '));

          // Load base company data
          setBaseData({
            name: companyData.name || '',
            website: companyData.website || '',
            address_street: companyData.address_street || '',
            address_house_number: companyData.address_house_number || '',
            address_zip_code: companyData.address_zip_code || '',
            address_city: companyData.address_city || '',
            address_country: companyData.address_country || '',
          });
        }
      } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error loading company settings:', error);
      }
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      loadSettings();
    }
  }, [companyId]);

  // Save sponsorship badge settings to Firebase
  const saveSponsorshipBadge = async (newBadgeSettings: SponsorshipBadgeSettings) => {
    setSaving(true);
    let saved = false;
    try {
      const companyRef = doc(db, 'companies', companyId);
      
      // Try updating with nested path first
      await updateDoc(companyRef, {
        'settings.sponsorshipBadge': newBadgeSettings
      });
      saved = true;
    } catch (error) {
      try {
        // Fallback: try updating the entire settings object
        const companyRef = doc(db, 'companies', companyId);
        const currentDoc = await getDoc(companyRef);
        if (currentDoc.exists()) {
          const currentData = currentDoc.data();
          const updatedSettings = {
            ...currentData.settings,
            sponsorshipBadge: newBadgeSettings
          };
          await updateDoc(companyRef, {
            settings: updatedSettings
          });
          saved = true;
        }
      } catch (fallbackError) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error saving sponsorship badge settings:', fallbackError);
      }
      }
    } finally {
      setSaving(false);
      if (saved) onCompanyUpdated?.();
    }
  };

  // Handle logo upload
  const handleLogoUpload = async (file: File) => {
    if (!file) return;

    setUploadingLogo(true);
    try {
      const storage = getStorage();
      const logoRef = ref(storage, `companies/${companyId}/sponsorship-badge.png`);
      
      // Upload the file
      await uploadBytes(logoRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(logoRef);
      
      // Update the sponsorship badge settings - automatically enable when logo is uploaded
      const newBadgeSettings = { ...sponsorshipBadge, logoUrl: downloadURL, enabled: true };
      setSponsorshipBadge(newBadgeSettings);
      await saveSponsorshipBadge(newBadgeSettings);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error uploading logo:', error);
      }
    } finally {
      setUploadingLogo(false);
    }
  };

  // Handle logo removal
  const handleLogoRemove = async () => {
    if (!sponsorshipBadge.logoUrl) return;

    setUploadingLogo(true);
    try {
      const storage = getStorage();
      const logoRef = ref(storage, `companies/${companyId}/sponsorship-badge.png`);
      
      // Delete the file from storage
      await deleteObject(logoRef);
      
      // Update the sponsorship badge settings - disable when logo is removed
      const newBadgeSettings = { ...sponsorshipBadge, logoUrl: '', enabled: false };
      setSponsorshipBadge(newBadgeSettings);
      await saveSponsorshipBadge(newBadgeSettings);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error removing logo:', error);
      }
    } finally {
      setUploadingLogo(false);
    }
  };


  // Handle file input change
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleLogoUpload(file);
    }
  };

  // Handle base data changes
  const handleBaseDataChange = (field: string, value: string | boolean) => {
    setBaseData(prev => ({ ...prev, [field]: value }));
  };

  // Save base data
  const saveBaseData = async () => {
    setSavingBaseData(true);
    try {
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, baseData);
      showToast(t('company_settings_base_data_saved') || 'Base data saved successfully', 'success');
      onCompanyUpdated?.();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error saving base data:', error);
      }
      showToast(t('company_settings_base_data_save_error') || 'Failed to save base data', 'error');
    } finally {
      setSavingBaseData(false);
    }
  };

  // Handle email domains input change
  const handleEmailDomainsInputChange = (value: string) => {
    setEmailDomainsInput(value);
  };

  // Save allowed email domains
  const saveEmailDomains = async () => {
    setSavingEmailDomains(true);
    try {
      // Parse domains from input (split by comma or newline, trim, filter empty)
      const domains = emailDomainsInput
        .split(/[,\n]/)
        .map(domain => domain.trim().toLowerCase())
        .filter(domain => domain.length > 0);

      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, {
        allowedEmailDomains: domains
      });
      
      setAllowedEmailDomains(domains);
      showToast(t('company_settings_email_domains_saved') || 'Email domains saved successfully', 'success');
      onCompanyUpdated?.();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error saving email domains:', error);
      }
      showToast(t('company_settings_email_domains_save_error') || 'Failed to save email domains', 'error');
    } finally {
      setSavingEmailDomains(false);
    }
  };


  if (loading) {
    return null;
  }

  return (
    <div className={styles.container}>
      {/* Base Data - Collapsible */}
      <GlobalCollapsibleButton 
        title={t('company_settings_base_data') || 'Base Data'}
        isOpen={false}
      >
        <div className={styles.settingsList}>
          {/* Company Name */}
          <div className={styles.settingItem}>
            <div className={styles.settingInfo}>
              <div className={styles.settingHeader}>
                <h4 className={styles.settingTitle}>
                  {t('company_settings_name') || 'Company Name'}
                </h4>
                <GlobalInput
                  label=""
                  value={baseData.name}
                  onChange={(e) => handleBaseDataChange('name', e.target.value)}
                  placeholder={t('company_settings_name_placeholder') || 'Enter company name'}
                  disabled={savingBaseData}
                />
              </div>
            </div>
          </div>

          {/* Website */}
          <div className={styles.settingItem}>
            <div className={styles.settingInfo}>
              <div className={styles.settingHeader}>
                <h4 className={styles.settingTitle}>
                  {t('company_settings_website') || 'Website'}
                </h4>
                <GlobalInput
                  label=""
                  value={baseData.website}
                  onChange={(e) => handleBaseDataChange('website', e.target.value)}
                  placeholder={t('company_settings_website_placeholder') || 'https://example.com'}
                  disabled={savingBaseData}
                />
              </div>
            </div>
          </div>

          {/* Address Street */}
          <div className={styles.settingItem}>
            <div className={styles.settingInfo}>
              <div className={styles.settingHeader}>
                <h4 className={styles.settingTitle}>
                  {t('company_settings_address_street') || 'Street'}
                </h4>
                <GlobalInput
                  label=""
                  value={baseData.address_street}
                  onChange={(e) => handleBaseDataChange('address_street', e.target.value)}
                  placeholder={t('company_settings_address_street_placeholder') || 'Street name'}
                  disabled={savingBaseData}
                />
              </div>
            </div>
          </div>

          {/* Address House Number */}
          <div className={styles.settingItem}>
            <div className={styles.settingInfo}>
              <div className={styles.settingHeader}>
                <h4 className={styles.settingTitle}>
                  {t('company_settings_address_house_number') || 'House Number'}
                </h4>
                <GlobalInput
                  label=""
                  value={baseData.address_house_number}
                  onChange={(e) => handleBaseDataChange('address_house_number', e.target.value)}
                  placeholder={t('company_settings_address_house_number_placeholder') || 'House number'}
                  disabled={savingBaseData}
                />
              </div>
            </div>
          </div>

          {/* Address ZIP Code */}
          <div className={styles.settingItem}>
            <div className={styles.settingInfo}>
              <div className={styles.settingHeader}>
                <h4 className={styles.settingTitle}>
                  {t('company_settings_address_zip_code') || 'ZIP Code'}
                </h4>
                <GlobalInput
                  label=""
                  value={baseData.address_zip_code}
                  onChange={(e) => handleBaseDataChange('address_zip_code', e.target.value)}
                  placeholder={t('company_settings_address_zip_code_placeholder') || 'ZIP code'}
                  disabled={savingBaseData}
                />
              </div>
            </div>
          </div>

          {/* Address City */}
          <div className={styles.settingItem}>
            <div className={styles.settingInfo}>
              <div className={styles.settingHeader}>
                <h4 className={styles.settingTitle}>
                  {t('company_settings_address_city') || 'City'}
                </h4>
                <GlobalInput
                  label=""
                  value={baseData.address_city}
                  onChange={(e) => handleBaseDataChange('address_city', e.target.value)}
                  placeholder={t('company_settings_address_city_placeholder') || 'City'}
                  disabled={savingBaseData}
                />
              </div>
            </div>
          </div>

          {/* Address Country */}
          <div className={styles.settingItem}>
            <div className={styles.settingInfo}>
              <div className={styles.settingHeader}>
                <h4 className={styles.settingTitle}>
                  {t('company_settings_address_country') || 'Country'}
                </h4>
                <GlobalInput
                  label=""
                  value={baseData.address_country}
                  onChange={(e) => handleBaseDataChange('address_country', e.target.value)}
                  placeholder={t('company_settings_address_country_placeholder') || 'Country'}
                  disabled={savingBaseData}
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className={styles.settingItem}>
            <GlobalButton
              variant="secondary"
              onClick={saveBaseData}
              disabled={savingBaseData}
              isLoading={savingBaseData}
            >
              {t('global_save')}
            </GlobalButton>
          </div>
        </div>
      </GlobalCollapsibleButton>

      {/* Corporate Identity - Collapsible */}
      <GlobalCollapsibleButton 
        title={t('company_settings_sponsorship_badge')}
        isOpen={false}
      >
        <div className={styles.settingsList}>
          {/* Badge Logo */}
          <div className={styles.settingItem}>
            <div className={styles.settingInfo}>
              <div className={styles.settingHeader}>
                <h4 className={styles.settingTitle}>
                  {t('company_settings_sponsorship_badge_logo')}
                </h4>
                
                {/* Current logo preview */}
                {sponsorshipBadge.logoUrl && (
                  <div className={styles.logoPreview}>
                    <img 
                      src={sponsorshipBadge.logoUrl} 
                      alt="Corporate identity logo" 
                      width="auto"
                      height={20}
                    />
                    <GlobalButton
                      variant="secondary"
                      onClick={handleLogoRemove}
                      disabled={uploadingLogo || saving}
                      isLoading={uploadingLogo}
                    >
                      {t('company_settings_sponsorship_badge_remove')}
                    </GlobalButton>
                  </div>
                )}

                {/* Upload button */}
                <GlobalButton
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo || saving}
                  isLoading={uploadingLogo}
                >
                  {t('company_settings_sponsorship_badge_upload')}
                </GlobalButton>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png"
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          </div>
        </div>
      </GlobalCollapsibleButton>

      {/* Microsoft SSO - Collapsible */}
      <GlobalCollapsibleButton 
        title={t('company_settings_microsoft_sso_title') || 'Microsoft SSO'}
        isOpen={false}
      >
        <div className={styles.settingsList}>
          <div className={styles.settingItem}>
            <div className={styles.settingInfo}>
              <div className={styles.settingHeader}>
                <h4 className={styles.settingTitle}>
                  {t('company_settings_allowed_email_domains') || 'Allowed Email Domains'}
                </h4>
                <p className={styles.settingDescription}>
                  {t('company_settings_allowed_email_domains_description') || 'Enter email domains (e.g., company.com, subsidiary.com) separated by commas. Users with Microsoft accounts from these domains will be automatically associated with this company when they sign in.'}
                </p>
                <GlobalTextarea
                  label={t('company_settings_allowed_email_domains_placeholder') || 'company.com, subsidiary.com, another-domain.com'}
                  value={emailDomainsInput}
                  onChange={(e) => handleEmailDomainsInputChange(e.target.value)}
                  disabled={savingEmailDomains}
                  rows={4}
                />
                {allowedEmailDomains.length > 0 && (
                  <div className={styles.domainsList}>
                    <p className={styles.settingDescription} style={{ marginTop: '8px', marginBottom: '4px' }}>
                      {t('company_settings_current_domains') || 'Current domains:'}
                    </p>
                    <ul className={styles.domainsListItems}>
                      {allowedEmailDomains.map((domain, index) => (
                        <li key={index}>{domain}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <GlobalButton
                  variant="secondary"
                  onClick={saveEmailDomains}
                  disabled={savingEmailDomains}
                  isLoading={savingEmailDomains}
                  style={{ marginTop: '12px' }}
                >
                  {t('global_save')}
                </GlobalButton>
              </div>
            </div>
          </div>
        </div>
      </GlobalCollapsibleButton>
    </div>
  );
};
